"use client";

/**
 * StagedQueue — THE SHELF: the queued notes live inside the composer's top band.
 *
 * Server-authoritative staged-message tray (K6 P5 D7). Cards appear ONLY while
 * the agent is actively processing a turn (MESSAGE_STAGED is emitted only on a
 * genuine mid-turn-busy send). The FE never mints an optimistic card; retract is
 * by uuid (idempotent), and a mount-time STAGING_SYNC request reconciles after a
 * reconnect. When the agent pulls a message in, the card clears and the message
 * moves into the thread.
 *
 * The shelf (founder 08-31, "loved the shelf"): a warm band across the top of the
 * composer so the queue reads as a PLACE, not a list. The header carries the
 * count and the promise ("for the next step" when the queue will steer the
 * running turn), and says `esc sends now` exactly once — Esc sends the WHOLE
 * queue, so a per-row "now" would promise something the system does not do.
 * A row is text plus two verbs: click the text to pull it back into the box and
 * edit it (a prefill + retract), or × to take it back.
 */
import { useEffect } from "react";
import { useTaskStore } from "@/stores/taskStore";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import type { StagedMessage, DepositChip } from "@/stores/types";

/** Only a note that is NOT yours wears a chip — the shelf is your notes by default. */
const ORIGIN_LABEL: Record<string, string> = {
  channel: "Channel",
  peer: "Agent",
  "auto-continuation": "System",
  initiative: "Proactive",
};

export function StagedQueue() {
  const taskId = useTaskStore((s) => s.selectedTaskId);
  const messages = useTaskStore((s) =>
    taskId ? s.stagedMessages.get(taskId) : undefined
  );
  // Phase 3 intelligent ingest: "saved to your brain" deposit chips. Empty
  // unless NEXT_PUBLIC_ENABLE_INTELLIGENT_INGEST is on (server gates the ack).
  const deposits = useTaskStore((s) =>
    taskId ? s.depositChips.get(taskId) : undefined
  );
  const cancelStaged = useTaskStore((s) => s.cancelStaged);
  const requestStagingSync = useTaskStore((s) => s.requestStagingSync);
  const requestPrefill = useComposerDraftStore((s) => s.requestPrefill);

  // Reconnect-resync: ask the server for the authoritative snapshot on mount /
  // task switch. An empty response clears any ghost cards (agent recycle).
  useEffect(() => {
    if (taskId) requestStagingSync(taskId);
  }, [taskId, requestStagingSync]);

  const hasStaged = Boolean(messages && messages.length > 0);
  const hasDeposits = Boolean(deposits && deposits.length > 0);
  if (!taskId || (!hasStaged && !hasDeposits)) return null;

  const count = messages?.length ?? 0;
  // 'now' = the queue steers the RUNNING turn at its next step; anything else
  // rides the normal pickup when the agent is ready. Say which, honestly.
  const steering = messages?.some((m) => m.priority === "now") ?? false;

  // Pull a note back into the box: the composer prefill carries the text, the
  // retract takes the card off the shelf. Editing is then just typing.
  const takeBack = (msg: StagedMessage) => {
    requestPrefill(taskId, msg.content);
    cancelStaged(taskId, msg.uuid);
  };

  return (
    <div className="shelf" role="list" aria-label="Queued messages">
      {hasStaged && (
        <div className="shelf__h">
          <span>
            <b>{count === 1 ? "1 note lined up" : `${count} notes lined up`}</b>
            {steering
              ? " for the next step"
              : " · sends when the agent is ready"}
          </span>
          <span className="shelf__esc hidden can-hover:inline-flex">
            <kbd>esc</kbd> sends now
          </span>
        </div>
      )}

      {messages?.map((msg: StagedMessage) => {
        const kind = msg.origin?.kind ?? "human";
        return (
          <div
            key={msg.uuid}
            role="listitem"
            data-testid="staged-queue-card"
            className="shelf__row"
          >
            {kind !== "human" && (
              <span className="shelf__origin">
                {ORIGIN_LABEL[kind] ?? kind}
              </span>
            )}
            <button
              type="button"
              className="shelf__tx"
              title="Pull it back into the box to edit"
              onClick={() => takeBack(msg)}
            >
              {msg.content}
            </button>
            <span className="shelf__edit" aria-hidden="true">
              click to edit
            </span>
            <button
              type="button"
              className="shelf__x"
              aria-label="Retract queued message"
              title="Remove from queue"
              onClick={() => cancelStaged(taskId, msg.uuid)}
            >
              ×
            </button>
          </div>
        );
      })}

      {deposits?.map((chip: DepositChip, i: number) => (
        <div
          key={chip.uuid ?? `deposit-${i}`}
          role="listitem"
          data-testid="staged-queue-deposit"
          className="shelf__row shelf__row--deposit"
        >
          <span className="shelf__origin">Saved to your brain</span>
          <span className="shelf__tx shelf__tx--static">{chip.label}</span>
        </div>
      ))}
    </div>
  );
}
